'use strict';

import { flow, action, observe, observable, configure, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import {getAggregateState} from '@uw-it-sis/lib-react/lib/StateUtils';

import {filter} from 'underscore';
import GenEdUtils from '@uw-it-sis/lib-react-myplan/lib/GenEdUtils';

import instructorsStore from './InstructorsStore';
import subjectAreasStore from './SubjectAreasStore';

/**
 * Store for course search text area autofill.
 *
 * This store aggregates instructor and subject area data.
 *
 */
class QueryStringTextFieldStore {

    state;

    constructor() {
        makeObservable(this, {
            state: observable,
            checkDataLoadStates: action
        });

        // This is the object to track the status of the loads.
        this.dataLoadStates = {
            instructors: States.initial,
            subjectAreas: States.initial
        };
    }

    mount() {
        /*
         * Observe the instructor and subjectArea stores' state props to know when data loads are complete.
         */
        this.instructorsStoreDisposer = observe(instructorsStore, "state", (change) => {
            // Set the data state for this store to the new value.
            this.dataLoadStates.instructors = change.newValue;
            // Precipitate a UI update if the data loads have completed.
            this.checkDataLoadStates();
        });

        this.subjectAreasDisposer = observe(subjectAreasStore, "state", (change) => {
            // Set the data state for this store to the new value.
            this.dataLoadStates.subjectAreas = change.newValue;
            // Precipitate a UI update if the data loads have completed.
            this.checkDataLoadStates();
        });

        // Kick off the data loads.
        subjectAreasStore.loadSubjectAreas();
        instructorsStore.loadInstructors();
    }

    unmount() {
        this.instructorsStoreDisposer();
        this.subjectAreasDisposer();
    }

    /**
     * Check aggregate data load states and update the overall state if necessary.
     */
    checkDataLoadStates() {
        this.state = getAggregateState(this.dataLoadStates);
    }

    /**
     * Gets all suggestions
     */
    getAllSuggestions() {
        return ([
            {
                title: "General Education Requirements",
                data: GenEdUtils.getList(),
                filterKeys: ["code", "title"]
            },
            {
                title: "Course Prefix & Curriculum",
                data: subjectAreasStore.subjectAreasList,
                filterKeys: ["code", "title"]
            },
            {
                title: "Instructors",
                data: instructorsStore.instructorsList,
                filterKeys: ["name"]
            }
        ]);
    }

    /**
     * Gets the subset of suggestions which contain the current query string.
     */
    getFilteredSuggestions(value, campus, isSectionSearch, limit) {

        let qs = value !== "" && value !== null ? value.value.toUpperCase() : "";
        let qsSanitized = qs.replace(/[#-.]|[[-^]|[?|{}]/g, '\\$&');
        let pattern = new RegExp("\\b" + qsSanitized, "i");
        let allSuggestions = this.getAllSuggestions();

        return allSuggestions.map(section => {
            let suggestions = [];

            // If the section suggestions data type is object (e.g. subject area or gen ed)
            if ( section.filterKeys && section.filterKeys.length > 1 ) {
                // Find suggestions with codes that begin with the query string. Also, check with spaces removed from code.
                let group1 = filter(section.data, function (suggestion) {
                    let key1 = suggestion[section.filterKeys[0]].toUpperCase();
                    let key1NoSpaces = suggestion[section.filterKeys[0]].replace(/\s/g, '').toUpperCase();
                    // If suggestion has the campus property, does it match the current selected campus, if no campus property (Gen Ed) then set to true
                    let useCampus = suggestion.campus ? (suggestion.campus === campus) : true;
                    // If suggestion has 'hideCatalog' prop and its true and the search is catalog, then set to false to hide from suggestions list
                    let showSuggestion = suggestion.hideCatalog ? isSectionSearch : true;

                    return ( ((key1.indexOf(qs) === 0) || (key1NoSpaces.indexOf(qs) === 0)) && useCampus && showSuggestion );
                });

                // Find suggestions with titles that contain the query string.
                let group2 = filter(section.data, function (suggestion) {
                    let key2 = suggestion[section.filterKeys[1]].toUpperCase();
                    // If suggestion has the campus property, does it match the current selected campus, if no campus property (Gen Ed) then set to true
                    let useCampus = suggestion.campus ? (suggestion.campus === campus) : true;
                    // If suggestion has 'hideCatalog' prop and its true and the search is catalog, then set to false to hide from suggestions list
                    let showSuggestion = suggestion.hideCatalog ? isSectionSearch : true;

                    return ( pattern.test(key2) && useCampus && showSuggestion );
                });

                // Filter duplicate suggestions out of titles list using codes list
                group2 = group2.filter(function (value) {
                    return group1.indexOf(value) === -1;
                });

                // Merge codes list and titles list
                suggestions = group1.concat(group2);
            } else {
                // Filtering of instructor names that match pattern and when search is time schedule
                if (isSectionSearch) {
                    suggestions = filter( section.data, function (suggestion) {
                        let key1 = suggestion[section.filterKeys[0]].toUpperCase();
                        // If suggestion has the campus property, does it match the current selected campus, if no campus property then set to true
                        let useCampus = suggestion.campus ? (suggestion.campus === campus) : true;

                        return ( pattern.test(key1) && useCampus );
                    }).sort( function(a, b)  {
                        if (a[section.filterKeys[0]] && b[section.filterKeys[0]]) {
                            let aKey = a[section.filterKeys[0]].toUpperCase(), bKey = b[section.filterKeys[0]].toUpperCase();
                            // Push results to the beginning based on where query string is found in suggestion
                            if (aKey.indexOf(qs) < bKey.indexOf(qs)) return -1;
                            if (aKey.indexOf(qs) > bKey.indexOf(qs)) return 1;
                            // Alphabetize if the index of the query string in the suggestions is the same
                            if (aKey < bKey) return -1;
                            if (aKey > bKey) return 1;
                            return 0;
                        } else {
                            return 0;
                        }
                    });
                }
            }

            // limit suggestions if limit is present
            suggestions = (limit ? suggestions.slice(0, limit) : suggestions);

            return {
                title: section.title,
                data: suggestions
            };

        }).filter(section => section.data.length > 0);
    }
}

export default new QueryStringTextFieldStore();